import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

/**
 * Payment files (ISO 20022 pain.001) — thin API client over
 * /:companyId/payments. Admin-only, feature `payment_files`.
 */
const opts = { withCredentials: true };
const url = (companyId, path) => getApiUrl(`${companyId}/payments/${path}`);

export const fetchQueue = (companyId) => axios.get(url(companyId, 'queue'), opts).then((r) => r.data);
export const addToQueue = (companyId, body) => axios.post(url(companyId, 'queue'), body, opts).then((r) => r.data);
export const removeFromQueue = (companyId, instructionId, reason) =>
  axios.delete(url(companyId, `queue/${instructionId}`), { ...opts, data: { reason } }).then((r) => r.data);
/** "Betalas på annat sätt": every queued salary row of one payroll leaves the queue. */
export const removePayrollFromQueue = (companyId, payrollId) =>
  axios.delete(url(companyId, `queue/payroll/${payrollId}`), opts).then((r) => r.data);
export const retryInstructions = (companyId, instructionIds) =>
  axios.post(url(companyId, 'queue/retry'), { instructionIds }, opts).then((r) => r.data);

export const previewFile = (companyId, body) => axios.post(url(companyId, 'files/preview'), body, opts).then((r) => r.data.preview);
export const previewFileXml = (companyId, body) => axios.post(url(companyId, 'files/preview-xml'), body, opts).then((r) => r.data);
export const createFile = (companyId, body) => axios.post(url(companyId, 'files'), body, opts).then((r) => r.data);
export const fetchFiles = (companyId, params = {}) => axios.get(url(companyId, 'files'), { ...opts, params }).then((r) => r.data.files);
export const fetchFile = (companyId, batchId) => axios.get(url(companyId, `files/${batchId}`), opts).then((r) => r.data.file);
export const markUploaded = (companyId, batchId) => axios.post(url(companyId, `files/${batchId}/uploaded`), {}, opts).then((r) => r.data.file);
export const cancelFile = (companyId, batchId, body) => axios.post(url(companyId, `files/${batchId}/cancel`), body, opts).then((r) => r.data.file);
export const cancelRemainder = (companyId, batchId, reason) =>
  axios.post(url(companyId, `files/${batchId}/cancel-remainder`), { reason }, opts).then((r) => r.data.file);

export const fetchProfiles = (companyId) => axios.get(url(companyId, 'profiles'), opts).then((r) => r.data.profiles);
export const fetchDebtorAccounts = (companyId) => axios.get(url(companyId, 'debtor-accounts'), opts).then((r) => r.data.accounts);
export const fetchDebtorAccount = (companyId, mappingId) => axios.get(url(companyId, `debtor-accounts/${mappingId}`), opts).then((r) => r.data);
export const updateDebtorAccount = (companyId, mappingId, body) =>
  axios.put(url(companyId, `debtor-accounts/${mappingId}`), body, opts).then((r) => r.data.account);

/** Download the stored XML and hand it to the browser as a file. */
export async function downloadFileContent(companyId, file) {
  const resp = await axios.get(url(companyId, `files/${file.batch_id}/content`), { ...opts, responseType: 'blob' });
  const disposition = resp.headers?.['content-disposition'] || '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const name = (match && match[1]) || file.file_name || `${file.batch_id}.xml`;
  const blob = new Blob([resp.data], { type: 'application/xml' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  return name;
}

/** Message + details from an API error, for toasts and inline errors. */
export function apiError(err, fallback = 'Något gick fel.') {
  const data = err?.response?.data;
  return { message: data?.message || fallback, details: data?.details || null, status: err?.response?.status || null };
}

// ---------------------------------------------------------------- session 2

/** Supplier payment methods — /:companyId/purchasing/suppliers/:id/payment-methods (admin writes). */
const methodsUrl = (companyId, supplierId, rest = '') => getApiUrl(`${companyId}/purchasing/suppliers/${supplierId}/payment-methods${rest}`);
export const fetchSupplierPaymentMethods = (companyId, supplierId) => axios.get(methodsUrl(companyId, supplierId), opts).then((r) => r.data);
export const createSupplierPaymentMethod = (companyId, supplierId, body) => axios.post(methodsUrl(companyId, supplierId), body, opts).then((r) => r.data);
export const updateSupplierPaymentMethod = (companyId, supplierId, methodId, body) =>
  axios.put(methodsUrl(companyId, supplierId, `/${methodId}`), body, opts).then((r) => r.data.method);
export const confirmSupplierPaymentMethod = (companyId, supplierId, methodId) =>
  axios.post(methodsUrl(companyId, supplierId, `/${methodId}/confirm`), {}, opts).then((r) => r.data);
export const retireSupplierPaymentMethod = (companyId, supplierId, methodId) =>
  axios.post(methodsUrl(companyId, supplierId, `/${methodId}/retire`), {}, opts).then((r) => r.data.method);

/** Skattekonto page: synced OCR, amount due and the tax rows already live. */
export const fetchSkatteverketSuggestion = (companyId) => axios.get(url(companyId, 'skatteverket/suggestion'), opts).then((r) => r.data);

/** Session 3: a return file from the bank (pain.002 status report or camt.054 debit notification). */
export const uploadReturnFile = (companyId, file) => {
  const form = new FormData();
  form.append('file', file, file.name);
  return axios.post(url(companyId, 'return-files'), form, { ...opts, headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data.result);
};

/** File page settlement (plan §3.11 intro): "Markera som betald" and exact-amount linking. */
export const markFilePaid = (companyId, batchId, body = {}) => axios.post(url(companyId, `files/${batchId}/mark-paid`), body, opts).then((r) => r.data);
export const markRowPaid = (companyId, batchId, instructionId, body = {}) =>
  axios.post(url(companyId, `files/${batchId}/rows/${instructionId}/mark-paid`), body, opts).then((r) => r.data);
export const linkRowPayment = (companyId, batchId, instructionId, paymentId) =>
  axios.post(url(companyId, `files/${batchId}/rows/${instructionId}/link-payment`), { payment_id: paymentId }, opts).then((r) => r.data);

/**
 * Derived chip for a supplier invoice from its live payment-file row
 * (plan §3.3: "i betalningskö" / "betalfil skapad" / "uppladdad" are never
 * stored on the invoice). Returns null when nothing is live.
 */
export function paymentFileChip(invoice) {
  const rowStatus = invoice?.payment_instruction_status;
  if (!rowStatus) return null;
  if (rowStatus === 'queued') return { key: 'queued', label: 'I betalningskö', tone: 'muted' };
  const fileStatus = invoice.payment_file_status;
  if (fileStatus === 'created') return { key: 'created', label: 'Betalfil skapad', tone: 'warning' };
  if (['uploaded', 'accepted', 'partially_accepted', 'partially_settled'].includes(fileStatus)) return { key: 'uploaded', label: 'Betalfil uppladdad', tone: 'info' };
  return { key: rowStatus, label: 'I betalfil', tone: 'info' };
}
